import React, { useState } from 'react';
import { Layout, Menu, Button, Dropdown, Grid } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  DashboardOutlined,
  UserOutlined,
  ShopOutlined,
  ApartmentOutlined,
  FileTextOutlined,
  BellOutlined,
  MenuOutlined,
  ApiOutlined,
  KeyOutlined,
} from '@ant-design/icons';
import { useAppSelector, useAppDispatch } from '../../hooks/redux';
import { logout } from '../../store/slices/authSlice';

const { Header, Content, Sider } = Layout;
const { useBreakpoint } = Grid;

interface AppLayoutProps {
  children: React.ReactNode;
}

const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const screens = useBreakpoint();
  const isMobile = !screens.lg;
  const [collapsed, setCollapsed] = useState(false);

  const isAdmin = user?.role === 'admin';

  const menuItems = [
    {
      key: '/dashboard',
      icon: <DashboardOutlined />,
      label: 'Dashboard',
    },
    ...(isAdmin
      ? [
          {
            key: '/admin/users',
            icon: <UserOutlined />,
            label: 'User Management',
          },
          {
            key: '/admin/regions',
            icon: <ApartmentOutlined />,
            label: 'Region Management',
          },
          {
            key: '/admin/stores',
            icon: <ShopOutlined />,
            label: 'Store Management',
          },
          {
            key: '/admin/audit-logs',
            icon: <FileTextOutlined />,
            label: 'Audit Logs',
          },
          {
            key: '/admin/alerts',
            icon: <BellOutlined />,
            label: 'Alert Management',
          },
          {
            key: '/admin/google-dashboard',
            icon: <ApiOutlined />,
            label: 'Google API Dashboard',
          },
          {
            key: '/admin/api-tokens',
            icon: <KeyOutlined />,
            label: 'API Tokens',
          },
        ]
      : []),
  ];

  const handleMenuClick = ({ key }: { key: string }) => {
    navigate(key);
    if (isMobile) setCollapsed(true);
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  return (
    <Layout style={{ minHeight: '100vh' }} className="app-layout">
      <Sider
        collapsible
        breakpoint="lg"
        collapsedWidth={0}
        collapsed={collapsed}
        onCollapse={setCollapsed}
        onBreakpoint={(broken) => {
          if (broken) setCollapsed(true);
        }}
        trigger={null}
      >
        <div
          style={{
            height: 32,
            margin: 16,
            background: 'rgba(255, 255, 255, 0.3)',
            borderRadius: 4,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontWeight: 'bold',
          }}
        >
          GBP Analytics
        </div>
        <Menu
          theme="dark"
          selectedKeys={[location.pathname]}
          mode="inline"
          items={menuItems}
          onClick={handleMenuClick}
        />
      </Sider>
      <Layout>
        <Header className="app-layout-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            {isMobile && (
              <Button
                type="text"
                icon={<MenuOutlined />}
                onClick={() => setCollapsed(!collapsed)}
                style={{ fontSize: 18 }}
              />
            )}
            <h2 className="app-layout-title">GBP Review Star Analytics</h2>
          </div>
          {isMobile ? (
            <Dropdown
              menu={{
                items: [
                  { key: 'email', label: user?.email, disabled: true },
                  { type: 'divider' },
                  { key: 'logout', label: 'Logout', onClick: handleLogout },
                ],
              }}
              trigger={['click']}
            >
              <Button type="text" icon={<UserOutlined />} />
            </Dropdown>
          ) : (
            <div>
              <span style={{ marginRight: 16 }}>{user?.email}</span>
              <button
                onClick={handleLogout}
                style={{
                  cursor: 'pointer',
                  background: 'none',
                  border: 'none',
                  color: 'inherit',
                  textDecoration: 'underline',
                  padding: 0,
                }}
              >
                Logout
              </button>
            </div>
          )}
        </Header>
        <Content className="app-layout-content">
          {isMobile && !collapsed && (
            <div
              className="app-layout-mask"
              onClick={() => setCollapsed(true)}
              aria-hidden="true"
            />
          )}
          {children}
        </Content>
      </Layout>
    </Layout>
  );
};

export default AppLayout;
